import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  Grid,
  ListItemText,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { DateRangePicker } from "@material-ui/pickers";
import * as C from "components";
import {
  setCurrentPage,
  getPicTreeRegularSeasonEventPageData,
  getPicTreeRegularSeasonEventBootstrapData,
  setPicTreeRegularSeasonEventPageData,
} from "actions";
import RegularSeasonKpiEventCards from "components/PicTree/RegularSeason/RegularSeasonKpiEventCards";
import RegularSeasonDailyTrendChart from "components/PicTree/RegularSeason/RegularSeasonDailyTrendChart";
import RegularSeasonEventTable from "components/PicTree/RegularSeason/RegularSeasonEventTable";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  breadcrumbs: {
    marginTop: 16,
    minHeight: 30,
    width: "100%",
    justifyContent: "flex-start",
  },
  topBar: {
    marginTop: -16,
    marginBottom: 8,
  },
  filterRow: {
    paddingTop: theme.spacing(1),
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
  },
  sectionGap: {
    marginTop: theme.spacing(2),
  },
  filters: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    overflowX: "hidden",
    rowGap: 8,
  },
  eventSelectWrap: {
    flex: "0 1 560px",
    minWidth: 0,
    maxWidth: "100%",
    //width: "100%",
  },
  eventAutocomplete: {
    width: "100%",
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "wrap",
    },
    "& .MuiAutocomplete-tag": {
      maxWidth: "100%",
      whiteSpace: "nowrap",
    },
    "& .MuiChip-label": {
      whiteSpace: "nowrap",
    },
  },
  dateRangeWrap: {
    display: "flex",
    alignItems: "center",
    flex: "0 1 276px",
    maxWidth: "100%",
    width: 276,
    marginTop: 0,
  },
  dateRangeInput: {
    display: "flex",
    alignItems: "baseline",
  },
  dateField: {
    width: 120,
  },
  dateDelimiter: {
    margin: "0 4px",
    color: "#556070",
  },
  goButton: {
    height: 32,
    minWidth: 60,
    paddingLeft: 18,
    paddingRight: 18,
    color: "#fff",
    fontSize: 15,
  },
}));

const ALL_BOOST_EVENTS_VALUE = "__ALL__";

const PicTreeRegularSeasonEventOverview = ({
  setCurrentPage,
  getPicTreeRegularSeasonEventPageData,
  getPicTreeRegularSeasonEventBootstrapData,
  selectedSeasonId = null,
  selectedSeasonIds = [],
  selectedBoostEventId = ALL_BOOST_EVENTS_VALUE,
  selectedEventKeys = [],
  draftSelectedEventKeys = null,
  reduxDraftDateRange = [null, null],
  selectedDateRange = [null, null],
  kpis = [],
  trend = {
    date: [],
    uniqueShots: [],
    reviewerOk: [],
    uniqueShooters: [],
  },
  trendLoading = false,
  trendRows = [],
  trendRowsLoading = false,
  eventRows = [],
  eventRowsLoading = false,
  eventRowsError = null,
  bootstrapEventReady = false,
  setPicTreeRegularSeasonEventPageData,
}) => {
  const classes = useStyles();
  const currentSeasonIds = Array.isArray(selectedSeasonIds) ? selectedSeasonIds : [];
  const [draftDateRange, setDraftDateRange] = useState(reduxDraftDateRange);
  const [initialDataCall, setInitialDataCall] = useState(true);
  const todayJst = moment().tz("Asia/Tokyo").format("YYYY-MM-DD");
  const didAutoSelectInitialEventRef = useRef(false);
  const requestedBootstrapSeasonIdRef = useRef(null);
  const draftDateRangeRef = useRef(draftDateRange);

  useEffect(() => {
    draftDateRangeRef.current = draftDateRange;
  }, [draftDateRange]);

  useEffect(() => {
    setCurrentPage(window.location.pathname);
    if (requestedBootstrapSeasonIdRef.current !== "catalog") {
      requestedBootstrapSeasonIdRef.current = "catalog";
      getPicTreeRegularSeasonEventBootstrapData({});
    }
  }, [getPicTreeRegularSeasonEventBootstrapData, setCurrentPage]);

  useEffect(() => {
    if (!Array.isArray(selectedDateRange) || selectedDateRange.length < 2) {
      return;
    }

    const [startDate, endDate] = selectedDateRange;
    if (!startDate || !endDate) {
      return;
    }

    const currentStartDate = draftDateRangeRef.current?.[0]?.format?.("YYYY-MM-DD");
    const currentEndDate = draftDateRangeRef.current?.[1]?.format?.("YYYY-MM-DD");
    if (currentStartDate === startDate && currentEndDate === endDate) {
      return;
    }

    setDraftDateRange([
      moment(startDate, "YYYY-MM-DD"),
      moment(endDate, "YYYY-MM-DD"),
    ]);
  }, [selectedDateRange]);

  const eventOptions = useMemo(() => {
    const rows = Array.isArray(eventRows) ? eventRows : [];
    const uniqueMap = new Map();
    rows.forEach((row) => {
      const key = row?.seasonId && row?.id ? `${row.seasonId}:${row.id}` : "";
      if (!key || uniqueMap.has(key)) {
        return;
      }
      uniqueMap.set(key, row);
    });

    return Array.from(uniqueMap.values()).map((row) => ({
      key: `${row.seasonId}:${row.id}`,
      gameSpaceId: row.seasonId,
      boostEventId: row.id,
      label: row.name || row.id,
      seasonName: row.seasonName || row.seasonId,
      seasonAbbreviation: row.seasonAbbreviation || "",
      period: row.period || "",
      startDatetime: row.startDatetime,
      endDatetime: row.endDatetime,
    }));
  }, [eventRows]);

  const draftEventKeys = Array.isArray(draftSelectedEventKeys)
    ? draftSelectedEventKeys
    : selectedEventKeys;
  const eventKeyOf = (event) => `${event?.gameSpaceId || ""}:${event?.boostEventId || ""}`;
  const selectedEventOptions = eventOptions.filter((option) => (
    draftEventKeys.some((event) => eventKeyOf(event) === option.key)
  ));

  const deriveEventDateRange = (options) => {
    const validOptions = (options || []).filter((option) => option.startDatetime && option.endDatetime);
    if (!validOptions.length) {
      return [null, null];
    }

    const start = validOptions.reduce((earliest, option) => (
      moment.utc(option.startDatetime).isBefore(earliest) ? moment.utc(option.startDatetime) : earliest
    ), moment.utc(validOptions[0].startDatetime));
    const latestEnd = validOptions.reduce((latest, option) => (
      moment.utc(option.endDatetime).isAfter(latest) ? moment.utc(option.endDatetime) : latest
    ), moment.utc(validOptions[0].endDatetime));
    const today = moment().tz("Asia/Tokyo").startOf("day");
    const startJst = start.tz("Asia/Tokyo").startOf("day");
    const cappedEnd = moment.min(latestEnd.tz("Asia/Tokyo").startOf("day"), today);
    if (cappedEnd.isBefore(startJst)) {
      return [today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")];
    }

    return [
      startJst.format("YYYY-MM-DD"),
      cappedEnd.format("YYYY-MM-DD"),
    ];
  };

  useEffect(() => {
    if (!selectedBoostEventId || selectedBoostEventId === ALL_BOOST_EVENTS_VALUE) {
      return;
    }
    if (!Array.isArray(selectedDateRange) || selectedDateRange.length < 2) {
      return;
    }

    const [startDate, endDate] = selectedDateRange;
    if (!startDate || !endDate) {
      return;
    }

    if (didAutoSelectInitialEventRef.current && selectedEventKeys.length === 1) {
      return;
    }

    didAutoSelectInitialEventRef.current = true;
    setDraftDateRange([
      moment(startDate, "YYYY-MM-DD"),
      moment(endDate, "YYYY-MM-DD"),
    ]);
  }, [
    currentSeasonIds,
    selectedEventKeys,
    selectedBoostEventId,
    selectedDateRange,
    selectedSeasonId,
  ]);

  useEffect(() => {
    if (!initialDataCall) {
      return;
    }
    if (!bootstrapEventReady) {
      return;
    }
    if (!selectedBoostEventId || selectedBoostEventId === ALL_BOOST_EVENTS_VALUE) {
      return;
    }
    if (!Array.isArray(selectedDateRange) || selectedDateRange.length < 2) {
      return;
    }

    const [startDate, endDate] = selectedDateRange;
    if (!startDate || !endDate) {
      return;
    }

    getPicTreeRegularSeasonEventPageData({
      selectedSeasonId: currentSeasonIds[0] || selectedSeasonId,
      selectedSeasonIds: currentSeasonIds,
      selectedBoostEventId,
      selectedEventKeys,
      selectedDateRange: [startDate, endDate],
      eventRows,
    });
    setInitialDataCall(false);
  }, [
    bootstrapEventReady,
    currentSeasonIds,
    getPicTreeRegularSeasonEventPageData,
    initialDataCall,
    eventRows,
    selectedEventKeys,
    selectedBoostEventId,
    selectedDateRange,
    selectedSeasonId,
  ]);

  const handleBoostEventChange = (_event, nextOptions) => {
    const nextKeys = nextOptions.map((option) => ({
      gameSpaceId: option.gameSpaceId,
      boostEventId: option.boostEventId,
    }));
    const nextDateRange = deriveEventDateRange(nextOptions);

    setPicTreeRegularSeasonEventPageData({
      draftSelectedEventKeys: nextKeys,
      draftDateRange: nextDateRange,
    });
    setDraftDateRange([
      nextDateRange[0] ? moment(nextDateRange[0], "YYYY-MM-DD") : null,
      nextDateRange[1] ? moment(nextDateRange[1], "YYYY-MM-DD") : null,
    ]);
  };

  const handleDateRangeChange = (nextValue) => {
    const nextRange = nextValue || [null, null];
    setDraftDateRange(nextRange);
    setPicTreeRegularSeasonEventPageData({
      draftDateRange: nextRange.map((date) => (date ? moment(date).format("YYYY-MM-DD") : null)),
    });
  };

  const handleGoClick = () => {
    const nextDateRange = Array.isArray(effectiveDateRange) && effectiveDateRange.length === 2
      ? effectiveDateRange
      : [null, null];
    const hasRange = nextDateRange[0] && nextDateRange[1];
    const appliedBoostEventId = draftEventKeys.length === 1
      ? draftEventKeys[0].boostEventId
      : ALL_BOOST_EVENTS_VALUE;
    const appliedDateRange = hasRange
      ? [
        moment(nextDateRange[0]).format("YYYY-MM-DD"),
        moment(nextDateRange[1]).format("YYYY-MM-DD"),
      ]
      : [null, null];

    setPicTreeRegularSeasonEventPageData({
      selectedEventKeys: draftEventKeys,
      selectedBoostEventId: appliedBoostEventId,
      selectedDateRange: appliedDateRange,
      draftSelectedEventKeys: draftEventKeys,
      draftDateRange: appliedDateRange,
    });

    if (!draftEventKeys.length) {
      return;
    }

    getPicTreeRegularSeasonEventPageData({
      selectedSeasonId: currentSeasonIds[0] || selectedSeasonId,
      selectedSeasonIds: currentSeasonIds,
      selectedBoostEventId: appliedBoostEventId,
      selectedEventKeys: draftEventKeys,
      eventRows,
      selectedDateRange: hasRange ? appliedDateRange : undefined,
    });
  };

  const effectiveDateRange = (Array.isArray(draftDateRange) && draftDateRange[0] && draftDateRange[1])
    ? draftDateRange
    : (
      Array.isArray(selectedDateRange) && selectedDateRange.length >= 2 && selectedDateRange[0] && selectedDateRange[1]
        ? [moment(selectedDateRange[0], "YYYY-MM-DD"), moment(selectedDateRange[1], "YYYY-MM-DD")]
        : [null, null]
    );

  return (
    <Fragment>
      <C.ContentWrapper>
        <Grid
          container
          spacing={1}
          direction="row"
          wrap="wrap"
          className={classes.topBar}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Grid item xs={12} md={4}>
            <Breadcrumbs separator="›" className={classes.breadcrumbs}>
              <Typography>PicTrée 全体</Typography>
              <Typography variant="body1">レギュラーシーズン</Typography>
              <Typography variant="body1">イベント</Typography>
              <Typography variant="body1">概要</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} md={8} className={classes.filterRow}>
            <Grid container spacing={1} alignItems="center" justifyContent="flex-end" className={classes.filters}>
              <Grid item className={classes.eventSelectWrap}>
                <Autocomplete
                  multiple
                  size="small"
                  className={classes.eventAutocomplete}
                  disableCloseOnSelect
                  options={eventOptions}
                  value={selectedEventOptions}
                  loading={eventRowsLoading}
                  disabled={eventRowsLoading}
                  limitTags={2}
                  getOptionLabel={(option) => option.label || option.boostEventId}
                  getOptionSelected={(option, value) => option.key === value.key}
                  noOptionsText={eventRowsError ? "イベント一覧の取得に失敗しました" : "選択可能なイベントがありません"}
                  loadingText="イベントを読み込み中"
                  onChange={handleBoostEventChange}
                  renderOption={(option, { selected }) => (
                    <React.Fragment>
                      <Checkbox
                        checked={selected}
                        color="primary"
                        style={{ marginRight: 8 }}
                        inputProps={{ "aria-label": `${option.label} ${option.seasonName} ${option.period}` }}
                      />
                      <ListItemText
                        primary={option.label}
                        secondary={`${option.seasonName || option.seasonAbbreviation || ""}${option.period ? ` / ${option.period}` : ""}`}
                      />
                    </React.Fragment>
                  )}
                  renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                    <Chip
                      label={`${option.label}${option.seasonAbbreviation ? ` (${option.seasonAbbreviation})` : ""}`}
                      size="small"
                      {...getTagProps({ index })}
                      key={option.key}
                    />
                  ))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      inputProps={{
                        ...params.inputProps,
                        "aria-label": "イベント",
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item className={classes.dateRangeWrap}>
                <DateRangePicker
                  startText="Start"
                  endText="End"
                  disableHighlightToday
                  showToolbar={false}
                  allowSameDateSelection
                  maxDate={todayJst}
                  value={effectiveDateRange}
                  onChange={handleDateRangeChange}
                  renderInput={(startProps, endProps) => (
                    <div className={classes.dateRangeInput}>
                      <TextField
                        {...startProps}
                        size="small"
                        className={classes.dateField}
                        helperText=""
                        InputLabelProps={{
                          ...(startProps?.InputLabelProps || {}),
                          shrink: true,
                        }}
                      />
                      <span className={classes.dateDelimiter}>~</span>
                      <TextField
                        {...endProps}
                        size="small"
                        className={classes.dateField}
                        helperText=""
                        InputLabelProps={{
                          ...(endProps?.InputLabelProps || {}),
                          shrink: true,
                        }}
                      />
                    </div>
                  )}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.goButton}
                  disableElevation
                  onClick={handleGoClick}
                >
                  GO
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.sectionGap}>
          <Grid item xs={12}>
            <RegularSeasonKpiEventCards kpis={kpis} />
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.sectionGap}>
          <Grid item xs={12}>
            <RegularSeasonDailyTrendChart trend={trend} loading={trendLoading} />
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.sectionGap}>
          <Grid item xs={12}>
            <RegularSeasonEventTable rows={trendRows} loading={trendRowsLoading} />
          </Grid>
        </Grid>
      </C.ContentWrapper>
    </Fragment>
  );
};

PicTreeRegularSeasonEventOverview.propTypes = {
  setCurrentPage: PropTypes.func,
  getPicTreeRegularSeasonEventPageData: PropTypes.func,
  getPicTreeRegularSeasonEventBootstrapData: PropTypes.func,
  selectedSeasonId: PropTypes.string,
  selectedSeasonIds: PropTypes.array,
  selectedBoostEventId: PropTypes.string,
  selectedEventKeys: PropTypes.array,
  draftSelectedEventKeys: PropTypes.array,
  reduxDraftDateRange: PropTypes.array,
  selectedDateRange: PropTypes.array,
  kpis: PropTypes.array,
  trend: PropTypes.object,
  trendLoading: PropTypes.bool,
  trendRows: PropTypes.array,
  trendRowsLoading: PropTypes.bool,
  eventRows: PropTypes.array,
  eventRowsLoading: PropTypes.bool,
  eventRowsError: PropTypes.object,
  bootstrapEventReady: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  loading: state.pictreeRegularSeasonEvent.loading,
  selectedSeasonId: state.pictreeRegularSeasonEvent.selectedSeasonId,
  selectedSeasonIds: state.pictreeRegularSeasonEvent.selectedSeasonIds,
  selectedBoostEventId: state.pictreeRegularSeasonEvent.selectedBoostEventId,
  selectedEventKeys: state.pictreeRegularSeasonEvent.selectedEventKeys,
  draftSelectedEventKeys: state.pictreeRegularSeasonEvent.draftSelectedEventKeys,
  reduxDraftDateRange: state.pictreeRegularSeasonEvent.draftDateRange,
  selectedCompanyCode: state.pictreeRegularSeasonEvent.selectedCompanyCode,
  kpis: state.pictreeRegularSeasonEvent.kpis,
  trend: state.pictreeRegularSeasonEvent.trend,
  trendLoading: state.pictreeRegularSeasonEvent.trendLoading,
  trendRows: state.pictreeRegularSeasonEvent.trendRows,
  trendRowsLoading: state.pictreeRegularSeasonEvent.trendRowsLoading,
  eventRows: state.pictreeRegularSeasonEvent.eventRows,
  eventRowsLoading: state.pictreeRegularSeasonEvent.eventRowsLoading,
  eventRowsError: state.pictreeRegularSeasonEvent.eventRowsError,
  selectedDateRange: state.pictreeRegularSeasonEvent.selectedDateRange,
  bootstrapEventReady: state.pictreeRegularSeasonEvent.bootstrapEventReady,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getPicTreeRegularSeasonEventPageData: (param) => dispatch(getPicTreeRegularSeasonEventPageData(param)),
  getPicTreeRegularSeasonEventBootstrapData: (param) => dispatch(getPicTreeRegularSeasonEventBootstrapData(param)),
  setPicTreeRegularSeasonEventPageData: (param) => dispatch(setPicTreeRegularSeasonEventPageData(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PicTreeRegularSeasonEventOverview);
